import { ApolloCache, gql, useQuery } from '@apollo/client';
import { IGridFormat, SortOrderEnum } from '../../../../.starryUI/globalTypes';
import { useQueryPromise } from '../../../../.starryUI/hooks/useQueryPromise';
import { IMainDirectoryListItem } from './useAllMainDirectoryListQuery';

const directoryFragment = gql`
    fragment MainDirectoryFragment on Directory {
        id
        isSelected
        applicationStatus_Description
        caption
        category_Description
        directoryFlagAccount
        directoryFlagMedical
        directoryFlagNotes
        directoryFlagPrivacy
        displayFlag
        email
        entryID
        entryStatus {
            value
        }
        event_Description
        extension_Description
        extension_Virtual
        group_Description
        groupID
        iD1
        iD2
        iD3
        iD4
        iD5
        incidentHold
        level1
        level2
        level3
        level4
        nameFirst
        nameInitials
        nameLast
        nameOther
        namePreferred
        roomLocation_Description
        roomSpace_Description
        roomType_Description
        situationResponse_Description
        situationResponseEnum
        genderEnum_Description
        termSession_Description
        classification_Description
        smallImage: image(maxSize: 32) {
            url
        }
        largeImage: image(maxSize: 500) {
            url
        }
    }
`;

const mainDirectoryQuery = gql`
    ${directoryFragment}

    query MainDirectory(
        $viewType: ViewTypeEnum
        $viewId: ID
        $sort: [DirectorySortInput]
        $limit: Int
        $skip: Int
        $filter: [DirectoryFilterInput]
        $loadWorkSpace: Boolean!
    ) {
        allMainDirectory(viewType: $viewType, viewId: $viewId, filter: $filter, limit: $limit, skip: $skip, sort: $sort) {
            totalCount
            currentView {
                id
                dateCreated {
                    js
                }
                dateModified {
                    js
                }
                description
                caption
                favourite
                folder
                listType
                parentID
                recordType
                securityUserID
                tableName
            }
            fixedFilters
            nodes {
                ...MainDirectoryFragment
            }
        }

        mainDirectoryWorkspace(dynamicListID: $viewId) @include(if: $loadWorkSpace) {
            filters {
                key
                values {
                    itemId
                    description
                }
            }
            gridFormat {
                id
                columnOrder
                columnWidth
                columnVisibility
            }
            isReportGeneratorAllowed
            isTooComplex
        }
    }
`;

const mainDirectoryItem = gql`
    ${directoryFragment}

    query MainDirectoryItem($id: ID!) {
        mainDirectory(id: $id) {
            ...MainDirectoryFragment
        }
    }
`;

export type DirectoryFieldsEnum =
    | 'ID'
    | 'DIRECTORY_ID'
    | 'ENTRY_ID'
    | 'CATEGORY_ID'
    | 'CATEGORY_DESCRIPTION'
    | 'APPLICATION_STATUS_ID'
    | 'APPLICATION_STATUS_DESCRIPTION'
    | 'EVENT_ID'
    | 'EVENT_DESCRIPTION'
    | 'GROUP_ID'
    | 'GROUP_DESCRIPTION'
    | 'ROOM_SPACE_ID'
    | 'ROOM_SPACE_DESCRIPTION'
    | 'EXTENSION_DESCRIPTION'
    | 'EXTENSION_VIRTUAL'
    | 'ROOM_LOCATION_ID'
    | 'ROOM_LOCATION_DESCRIPTION'
    | 'ROOM_TYPE_ID'
    | 'ROOM_TYPE_DESCRIPTION'
    | 'ENTRY_STATUS_ENUM'
    | 'ENTRY_STATUS_DESCRIPTION'
    | 'PIN_NUMBER'
    | 'DIRECTORY_FLAG_NOTES'
    | 'DIRECTORY_FLAG_MEDICAL'
    | 'DIRECTORY_FLAG_PRIVACY'
    | 'DIRECTORY_FLAG_ACCOUNT'
    | 'INCIDENT_HOLD'
    | 'NAME_LAST'
    | 'NAME_FIRST'
    | 'NAME_TITLE'
    | 'NAME_PREFERRED'
    | 'NAME_OTHER'
    | 'NAME_INITIALS'
    | 'GENDER_ENUM'
    | 'I_D_1'
    | 'I_D_2'
    | 'I_D_3'
    | 'I_D_4'
    | 'I_D_5'
    | 'LEVEL_1'
    | 'LEVEL_2'
    | 'LEVEL_3'
    | 'LEVEL_4'
    | 'SITUATION_RESPONSE_ENUM'
    | 'SITUATION_RESPONSE_DETAIL'
    | 'SITUATION_RESPONSE_DESCRIPTION'
    | 'IS_NON_ACTIVE_EVENT_OR_GROUP'
    | 'EMAIL';

export type ViewTypeEnum = 'NONE' | 'SAVED_LIST' | 'DYNAMIC_LIST' | 'ALL_ENTRIES' | 'ACTIVE_ENTRIES';

export interface IDirectoryVariablesSorting {
    field: DirectoryFieldsEnum;
    order: SortOrderEnum;
}

export interface IDirectoryVariables {
    limit?: number;
    skip?: number;
    filter?: IDirectoryFilter[];
    sort?: IDirectoryVariablesSorting[];
    viewType?: ViewTypeEnum;
    viewId?: number;
    loadWorkSpace: boolean;
}

export interface IDirectoryNodes {
    id: string;
    isSelected: boolean;
    applicationStatus_Description: string;
    caption: string;
    category_Description: string;
    directoryFlagNotes: boolean;
    directoryFlagMedical: boolean;
    directoryFlagPrivacy: boolean;
    directoryFlagAccount: boolean;
    displayFlag: boolean;
    email: string;
    entryID: string;
    entryStatus: {
        value: string;
    };
    event_Description: string;
    extension_Description: string;
    extension_Virtual: string;
    group_Description: string;
    groupID: string;
    iD1: string;
    iD2: string;
    iD3: string;
    iD4: string;
    iD5: string;
    incidentHold: boolean;
    level1: string;
    level2: string;
    level3: string;
    level4: string;
    nameFirst: string;
    nameInitials: string;
    nameLast: string;
    nameOther: string;
    namePreferred: string;
    roomLocation_Description: string;
    roomSpace_Description: string;
    roomType_Description: string;
    situationResponseEnum: starrez.SituationResponseEnum;
    situationResponse_Description: string;
    genderEnum_Description: string;
    termSession_Description: string;
    classification_Description: string;
    smallImage: {
        url: string;
    };
    largeImage: {
        url: string;
    };
}

export interface IMainDirectoryQueryResults {
    allMainDirectory: {
        totalCount: number;
        fixedFilters?: string[]; // Please note that this field will be moved to directory workspace with approperiate type
        nodes: IDirectoryNodes[];
        currentView: IMainDirectoryListItem;
    };

    mainDirectoryWorkspace?: {
        filters?: [IDirectoryFilterFromServer];
        gridFormat?: [IGridFormat];
        isReportGeneratorAllowed: boolean;
        isTooComplex: boolean;
    };
}

// Ideally we can make server to return same format as IDirectoryFilter
export interface IDirectoryFilterFromServer {
    key: string;
    values: {
        itemId: string;
        description: string;
    }[];
}

export interface IDirectoryFilter {
    applicationStatusID?: { in?: string[] };
    appliedForTermID?: { in?: string[] };
    bookedForTermID?: { in?: string[] };
    categoryID?: { in?: string[] };
    communityFilter?: { in?: string[] };
    entryStatus?: { in?: string[] };
    eventID?: { in?: string[] };
    gender?: { in?: string[] };
    groupID?: { in?: string[] };
    roomTypeID?: { in?: string[] };
    termSessionID?: { in?: string[] };
    search?: { like?: string };
}

export type EntryStatusEnum =
    | 'ADMIN'
    | 'TENTATIVE'
    | 'RESERVED'
    | 'HELD'
    | 'IN_ROOM'
    | 'HISTORY'
    | 'OCCUPANT'
    | 'OCCUPANT_HISTORY'
    | 'OCCUPANT_IN_ROOM'
    | 'ATTENDEE'
    | 'ATTENDEE_ARRIVED'
    | 'ATTENDEE_DEPARTED'
    | 'ACCOUNT'
    | 'MASTER_ACCOUNT'
    | 'CANCELLED'
    | 'ERASED'
    | 'APPLICATION'
    | 'INCIDENT'
    | 'CONTACT'
    | 'ALUMNI';

export const useMainDirectoryQuery = (variables: IDirectoryVariables) =>
    useQuery<IMainDirectoryQueryResults, IDirectoryVariables>(mainDirectoryQuery, { variables, fetchPolicy: 'cache-first' });

export const useMainDirectoryItem = () => useQueryPromise<{ mainDirectory: IDirectoryNodes }, { id: string }>(mainDirectoryItem, 'network-only');

export const writeMainDirectoryCache = (cache: ApolloCache<unknown>, data: IDirectoryNodes) => {
    cache.writeQuery({
        query: gql`
            ${directoryFragment}

            query UpdateMainDirectoryCache {
                ...MainDirectoryFragment
            }
        `,
        id: `Directory:${data.id}`,
        data,
        overwrite: true,
    });
};
